import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _toConsumableArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Copyright (c) 2021 lingdocs.com
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useState, useEffect } from "react";
import Pashto from "./Pashto";
import Phonetics from "./Phonetics";
import { psStringEquals, isAllOne, addEnglish } from "../lib/p-text-helpers";
import { isSentenceForm } from "../lib/misc-helpers";

var arrowDown = _jsx("svg", Object.assign({
  xmlns: "http://www.w3.org/2000/svg",
  width: "20",
  height: "20",
  fill: "currentColor",
  className: "bi bi-caret-down",
  viewBox: "0 0 20 20"
}, {
  children: _jsx("path", {
    fillRule: "evenodd",
    d: "M3.204 5L8 10.481 12.796 5H3.204zm-.753.659l4.796 5.48a1 1 0 0 0 1.506 0l4.796-5.48c.566-.647.106-1.659-.753-1.659H3.204a1 1 0 0 0-.753 1.659z"
  }, void 0)
}), void 0);

var genderAbbrev = function genderAbbrev(gender) {
  return gender === "masc" ? " m." : gender === "fem" ? " f." : "";
};

var minifyTableGender = function minifyTableGender(block) {
  // @ts-ignore
  return block.reduce(function (table, person, i, src) {
    var isFem = i % 2 !== 0;

    if (isFem) {
      return table;
    }

    var femPersAhead = src[i + 1];
    var femPersIsTheSame = psStringEquals(person[0][0], femPersAhead[0][0]) && psStringEquals(person[1][0], femPersAhead[1][0]);

    if (femPersAhead && !femPersIsTheSame) {
      return [].concat(_toConsumableArray(table), [{
        masc: person,
        fem: femPersAhead
      }]);
    }

    return [].concat(_toConsumableArray(table), [person]);
  }, []);
};

function Cell(_ref) {
  var item = _ref.item,
      textOptions = _ref.textOptions,
      center = _ref.center,
      noBorder = _ref.noBorder;

  var _useState = useState(0),
      _useState2 = _slicedToArray(_useState, 2),
      version = _useState2[0],
      setVersion = _useState2[1];

  useEffect(function () {
    return setVersion(0);
  }, [item]);

  function advanceVersion() {
    setVersion((version + 1) % item.length);
  }

  var w = item[version] || item[0];
  return _jsx("td", Object.assign({
    style: noBorder ? {
      border: "none"
    } : {}
  }, {
    children: _jsxs("div", Object.assign({
      style: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: center ? "center" : "space-between",
        alignItems: "center"
      }
    }, {
      children: [_jsxs("div", {
        children: [_jsx("div", {
          children: _jsx(Pashto, Object.assign({
            opts: textOptions
          }, {
            children: w
          }), void 0)
        }, void 0), _jsx("div", {
          children: _jsx(Phonetics, Object.assign({
            opts: textOptions
          }, {
            children: w
          }), void 0)
        }, void 0), w.e && _jsx("div", Object.assign({
          className: "text-muted"
        }, {
          children: w.e
        }), void 0)]
      }, void 0), item.length > 1 && _jsxs("button", Object.assign({
        className: "btn btn-sm btn-light mx-2",
        onClick: advanceVersion
      }, {
        children: ["ver. ", version + 1, "/", item.length, " ", arrowDown]
      }), void 0)]
    }), void 0)
  }), void 0);
}

function VerbTable(_ref2) {
  var block = _ref2.block,
      textOptions = _ref2.textOptions,
      english = _ref2.english;
  var blockWEng = english ? addEnglish(english, block) : block;

  if (isSentenceForm(blockWEng) || isAllOne(blockWEng)) {
    var item = isSentenceForm(blockWEng) ? block : function () {
      var b = block;
      return b[0][0];
    }();
    return _jsx("table", Object.assign({
      className: "table text-center"
    }, {
      children: _jsx("tbody", {
        children: _jsx("tr", {
          children: _jsx(Cell, {
            item: item,
            textOptions: textOptions,
            center: true,
            noBorder: true
          }, void 0)
        }, void 0)
      }, void 0)
    }), void 0);
  }

  var bl = blockWEng;
  var b = minifyTableGender(bl);
  return _jsxs("table", Object.assign({
    className: "table mt-2",
    style: {
      tableLayout: "fixed"
    }
  }, {
    children: [_jsx("thead", {
      children: _jsxs("tr", {
        children: [_jsx("th", {
          scope: "col",
          style: {
            width: "3rem"
          }
        }, void 0), _jsx("th", Object.assign({
          scope: "col"
        }, {
          children: "Singular"
        }), void 0), _jsx("th", Object.assign({
          scope: "col"
        }, {
          children: "Plural"
        }), void 0)]
      }, void 0)
    }, void 0), _jsx("tbody", {
      children: b.reduce(function (rows, person, i, arr) {
        function drawRow(_ref3) {
          var line = _ref3.line,
              gender = _ref3.gender;
          var pers = arr.length > 1 ? ["1st", "2nd", "3rd"] : ["2nd"];
          var rowLabel = "".concat(pers[i]).concat(genderAbbrev(gender));
          var color = !gender ? "inherit" : gender === "masc" ? "#78c8ed" : "#ff99aa";
          return _jsxs("tr", {
            children: [_jsx("th", Object.assign({
              scope: "row",
              style: {
                color: color
              }
            }, {
              children: rowLabel
            }), void 0), _jsx(Cell, {
              item: line[0],
              textOptions: textOptions
            }, void 0), _jsx(Cell, {
              item: line[1],
              textOptions: textOptions
            }, void 0)]
          }, "".concat(i).concat(gender));
        }

        return "masc" in person ? [].concat(_toConsumableArray(rows), [drawRow({
          line: person.masc,
          gender: "masc"
        }), drawRow({
          line: person.fem,
          gender: "fem"
        })]) : [].concat(_toConsumableArray(rows), [drawRow({
          line: person
        })]);
      }, [])
    }, void 0)]
  }), void 0);
}

export default VerbTable;